import CalendarIcon from "@shared/svg/calendar-icon.svg?react";
import CalendarComponent, { CalendarValueType } from "@shared/ui/Calendar";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import { ErrorMessage, InputLabel } from "@shared/ui/Inputs";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const DROP_POSITIONS = {
  AUTO: "auto",
  BOTTOM: "bottom",
} as const;

export type DropPositionType =
  (typeof DROP_POSITIONS)[keyof typeof DROP_POSITIONS];

export const DROP_ALIGNMENTS = {
  LEFT: "left",
  RIGHT: "right",
} as const;

export type DropAlignmentType =
  (typeof DROP_ALIGNMENTS)[keyof typeof DROP_ALIGNMENTS];

type DateInputPropType = {
  wrapperClassName?: string;
  label?: string;
  keyId: string;
  uppercaseLabel?: boolean;
  errorMessage?: string;
  value?: string;
  showFormat?: boolean;
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  dropdownClassName?: string;
  dropPosition: DropPositionType;
  dropAlignment?: DropAlignmentType;
  scrollContainerSelector?: string;
  maxDate?: Date;
  minDate?: Date;
  onChange: (value: CalendarValueType) => void;
  onClose?: () => void;
  calendarClassName?: string;
  inputClassName?: string;
  autoOpen?: boolean;
};

const DateInput = ({
  wrapperClassName = "",
  label = "",
  keyId,
  uppercaseLabel = false,
  errorMessage = "",
  value = "",
  showFormat = false,
  format = "DD-MM-YYYY",
  placeholder = "",
  disabled = false,
  dropdownClassName = "",
  dropPosition = DROP_POSITIONS.BOTTOM,
  dropAlignment = DROP_ALIGNMENTS.LEFT,
  scrollContainerSelector = "",
  maxDate = null,
  minDate = null,
  onChange,
  onClose,
  calendarClassName = "",
  inputClassName = "",
  showActionButtons = false,
  autoOpen = false,
  ...props
}: React.ComponentPropsWithoutRef<"input"> &
  DateInputPropType & { showActionButtons?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const [showCalendar, setShowCalendar] = useState(false);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const [tempValue, setTempValue] = useState<CalendarValueType>(value);

  const autoOpenFiredRef = useRef(false);
  useEffect(() => {
    if (autoOpen && !disabled && !autoOpenFiredRef.current) {
      autoOpenFiredRef.current = true;
      handleOpenDropdown();
    }
  }, []);

  const classes: string = [
    "w-full h-4xl px-md rounded-lg font-manrope font-medium text-xs text-primary border border-solid transition-all duration-200 ease-in-out",
    "placeholder:text-secondary placeholder:opacity-100 placeholder:disabled:text-disabled disabled:placeholder:opacity-100",
    "focus:bg-primary focus:border-brand focus:outline-0",
    "disabled:text-disabled disabled:bg-accent disabled:border-transparent disabled:opacity-100",
    errorMessage
      ? "bg-primary border-danger focus:bg-primary focus:border-danger"
      : value
      ? "bg-primary border-primary"
      : "bg-accent border-transparent",
    inputClassName,
  ].join(" ");

  const isInvalidDate = useMemo(
    () => new Date(value)?.toString() === "Invalid Date",
    [value],
  );

  const updatePortalPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();

    const calendarHeight =
      calendarRef.current?.getBoundingClientRect().height || 380;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove =
      dropPosition === DROP_POSITIONS.AUTO &&
      spaceBelow < calendarHeight &&
      rect.top > calendarHeight;

    setPortalStyle({
      position: "fixed",
      width: "auto",
      zIndex: 9999,
      ...(dropAlignment === DROP_ALIGNMENTS.RIGHT
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left }),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
    });
  }, [dropPosition, dropAlignment]);

  const handleOpenDropdown = () => {
    if (disabled) return;
    setTempValue(value);
    updatePortalPosition();
    setShowCalendar(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
        onClose?.();
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Stop mousedown/touchstart from reaching parent click-outside handlers
  useEffect(() => {
    const el = calendarRef.current;
    if (!showCalendar || !el) return;

    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener("mousedown", stop);
    el.addEventListener("touchstart", stop);

    return () => {
      el.removeEventListener("mousedown", stop);
      el.removeEventListener("touchstart", stop);
    };
  }, [showCalendar]);

  // Reposition the portal calendar on scroll / resize while open
  useEffect(() => {
    if (!showCalendar) return;

    const scrollContainerElement = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : null;

    window.addEventListener("scroll", updatePortalPosition, true);
    window.addEventListener("resize", updatePortalPosition);
    if (scrollContainerElement) {
      scrollContainerElement.addEventListener("scroll", updatePortalPosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePortalPosition, true);
      window.removeEventListener("resize", updatePortalPosition);
      if (scrollContainerElement) {
        scrollContainerElement.removeEventListener(
          "scroll",
          updatePortalPosition,
        );
      }
    };
  }, [showCalendar, scrollContainerSelector, updatePortalPosition]);

  useEffect(() => {
    if (showCalendar) {
      updatePortalPosition();
    }
  }, [showCalendar, updatePortalPosition]);

  return (
    <div className={`flex flex-col gap-sm ${wrapperClassName || ""}`}>
      {label && (
        <div className="w-fit">
          <InputLabel
            label={label}
            keyId={keyId}
            uppercaseLabel={uppercaseLabel}
          />
        </div>
      )}

      <div
        ref={ref}
        className="relative flex items-center"
        onFocus={handleOpenDropdown}
      >
        <input
          id={keyId}
          readOnly
          disabled={disabled}
          className={classes}
          value={
            showFormat ? value || "" : value ? moment(value).format(format) : ""
          }
          placeholder={
            showFormat && !placeholder?.length ? "yyyy/mm/dd" : placeholder
          }
          {...props}
        />
        <div
          className="absolute right-md flex items-center text-secondary cursor-pointer"
          onClick={handleOpenDropdown}
        >
          <CalendarIcon width={16} height={16} />
        </div>
      </div>

      {showCalendar &&
        createPortal(
          <div
            ref={calendarRef}
            style={portalStyle}
            className={`p-lg bg-primary rounded-lg shadow-alert ${dropdownClassName}`}
          >
            <CalendarComponent
              calendarType="ISO 8601"
              showNeighboringMonth={false}
              className={calendarClassName}
              value={
                isInvalidDate || !tempValue
                  ? new Date()
                  : new Date(tempValue as any)
              }
              maxDate={maxDate}
              minDate={minDate}
              onChange={(v) => {
                if (showActionButtons) {
                  setTempValue(v);
                } else {
                  setShowCalendar(false);
                  onChange(v);
                }
              }}
            />
            {showActionButtons && (
              <div className="mt-lg flex items-center justify-between">
                <Button
                  variant={BUTTON_VARIANTS.LINK}
                  size={BUTTON_SIZES.SMALL}
                  text="Discard"
                  className="!text-brand !font-semibold"
                  onClick={() => {
                    setShowCalendar(false);
                    onChange("");
                  }}
                />
                <div className="flex gap-sm">
                  <Button
                    variant={BUTTON_VARIANTS.OUTLINE}
                    size={BUTTON_SIZES.SMALL}
                    text="Cancel"
                    onClick={() => {
                      setShowCalendar(false);
                      onClose?.();
                    }}
                  />
                  <Button
                    variant={BUTTON_VARIANTS.PRIMARY}
                    size={BUTTON_SIZES.SMALL}
                    text="Update"
                    onClick={() => {
                      setShowCalendar(false);
                      onChange(tempValue);
                    }}
                  />
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}

      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};

export default DateInput;
